import {
  AddReferralPropsType,
  defaultStateFieldsValues,
  ReferralPayload,
  REFERRAL_CREATE_ERROR_MESSAGE,
  performerCategoryFromMetaTag,
  filterResources,
  internalExternalOrg,
} from './constants';
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import {
  useConditions,
  useDocumentReferences,
  useGoals,
  useLocations,
  useObservations,
} from 'src/careflow/ph-core/fhir-client/fhir-react';
import { useSnackbar } from 'notistack';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { isArray, isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Iconify from 'src/components/Iconify';
import SearchServices from './SearchServices';
import CustomModal, { GridItem, GridSection } from 'src/components/CustomModal';
import useObjectState from 'src/hooks/useObjectState';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import {
  CodeableConcept,
  ServiceRequest,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import SearchMember from 'src/careflow/care-coordination/case/detail/components/SearchMember/SearchMember';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { ReferenceResources } from 'src/careflow/helpers/types';
import {
  OrganizationWrapper,
  WrappedOrganization,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import {
  HealthcareServiceWrapper,
  WrappedHealthcareService,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/HealthcareService';
import {
  ReferralService,
  ServiceByFiltersResponse,
} from 'src/crs/referral/services/ReferralService';
import AnnotationsModal from 'src/careflow/common/AnnotationsModal';
import AnnotationsList from 'src/careflow/common/AnnotationsList';
import InfoRibbon from '../../../components/InfoRibbon';
import {
  InfoRibbonPatient,
  InfoRibbonServiceRequest,
} from '../../../components/InfoRibbonSections';
import SelectSearchMember from 'src/careflow/common/SelectSearchMember';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import {
  getExternalResourceType,
  getOwnerDisplayLabel,
  getTaskOwnerByReference,
} from 'src/careflow/care-coordination/task/patient/TasksGrid/TaskModal';
import { getReferenceResourceId, fetchOrganizationAffiliation } from 'src/careflow/helpers/common';
import { getReferenceId } from 'src/utils/fhir';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import useServiceRequestContext from 'src/careflow/care-coordination/inbound-referral/useServiceRequestContext';
import {
  WrappedObservation,
  ObservationWrapper,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import {
  WrappedCondition,
  ConditionWrapper,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { GoalWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Goal';
import { getIdFromURI } from 'src/careflow/YoCare/utils/helpers';
import { PractitionerWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import NewPractitionerModal from 'src/careflow/care-coordination/practitioner/provider/PractitionerList/NewPractitionerModal/NewPractitionerModal';
import CreatePractitionerRoleDialog from 'src/careflow/care-coordination/practitioner/provider/PractitionerDetails/CreatePractitionerRoleDialog';
import {
  getCategoryChipStyle,
  isInternalCategory,
  filterOutInternalCategories,
} from 'src/careflow/care-coordination/referral/utils/categoryUtils';
import { PractitionerRoleWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import NewServiceModal from './NewServiceModal';
import { fhirClient } from 'src/App';
import { UserService } from 'src/careflow/ph-core/admin/user/UserService';
import WorkflowForm from './WorkflowForm';

const AddReferral = (props: AddReferralPropsType) => {
  const { i18n } = useLocales();
  const { componentsData, configurations } = useTenantConfigData();
  const { searchMemberOptions } = componentsData?.searchMember ?? {};
  const { addresses: addressesFilter } = componentsData?.referral ?? {};
  const {
    addServiceManually,
    addPerformerManually,
    serviceRequired,
    performerRequired,
    performerCategory: performerCategoryValues,
    showServicesCheckbox,
  } = componentsData?.referral ?? {};

  const {
    valueSets: [
      requestIntentVS,
      requestCategoryVS,
      referralServicesInternal,
      referralPathCategory,
      referralStatus,
    ],
    isLoading: isLoadingVS,
  } = useValueSetsByIdentifiers([
    'request-intent',
    'ph-request-category',
    'ph-referral-services-internal',
    'ph-service-request-path-category',
    'service-request-status',
  ]);

  const {
    title,
    referral: propReferral,
    patient,
    serviceRequest,
    carePlan,
    openAdd,
    onCancel,
    onReferralSuccess,
    externalValues,
    conditions,
    observations,
    task,
    showWorkflowScope,
  } = props;

  const { enqueueSnackbar } = useSnackbar();
  const authUser = useAuth().getCurrentUser();
  const { observations: observationSuggestions, conditions: conditionSuggestions } =
    useServiceRequestContext();
  const [state, updateState] = useObjectState<any>({});

  const [isHealthcareLoading, setIsHealthcareLoading] = useState(true);
  const [healthCareServices, setHealthCareServices] = useState<
    {
      label: string;
      value: {
        healthcareService: WrappedHealthcareService | CodeableConcept | undefined;
        organization: WrappedOrganization | undefined;
      };
    }[]
  >([]);
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [annotations, setAnnotations] = useState<any[]>([]);
  const [annotationsOpen, setAnnotationsOpen] = useState(false);
  const [planDefinition, setPlanDefinition] = useState<any[]>([]);

  const [internalExternal, setInternalExternal] = useState<string[]>(['all']);
  const [filteredHealthCareServices, setFilteredHealthCareServices] = useState<any[]>([]);

  // Practitioner creation state
  const [isNewPractitionerModalOpen, setIsNewPractitionerModalOpen] = useState(false);
  const [openPractitionerRolModal, setOpenPractitionerRolModal] = useState(false);
  const [practitioner, setPractitioner] = useState<any>(null);

  // Service creation state
  const [isNewServiceModalOpen, setIsNewServiceModalOpen] = useState(false);
  const [pendingServiceName, setPendingServiceName] = useState<string>('');

  const {
    date,
    startHour,
    serviceTypeItem,
    referredToItem,
    referredFromItem,
    isSearchMemberOpen,
    status,
    statusItem,
    users,
    assignedToItem,
    locationOrganization,
    file,
    selectedFile,
    isLoading,
    openSearch,
    intent,
    category,
    instantiatesCanonical,
    inputSelected,
    error,
    performerCategory,
    addresses,
    workflowScope,
  } = state;

  const serviceDirectory = performerCategory?.value === 'service-directory';
  const planDefinitionInternal = instantiatesCanonical?.planDefinitionType === 'internal';
  const showComponent = instantiatesCanonical?.planDefinitionType
    ? !planDefinitionInternal
    : performerCategory?.value
    ? serviceDirectory
    : !instantiatesCanonical?.planDefinitionType && !performerCategory?.value;

  const addressesAll = useMemo(() => {
    const addressesFromResources = [...(conditions ?? []), ...(observations ?? [])];
    const addressesFiltered = filterResources(addressesFromResources, {
      Condition: {
        values: addressesFilter?.conditionFilter ?? [],
        extractor: (item) => getIdFromURI(item.code?.coding?.[0]?.system ?? '') ?? undefined,
      },
      Observation: {
        values: addressesFilter?.observationFilter ?? [],
        extractor: (item) => item.category?.[0]?.coding?.[0]?.code,
      },
    });

    const getAddressLabel = (option: WrappedCondition | WrappedObservation) => {
      let baseLabel = '';
      let priorityInfo = '';
      if (option.resourceType === 'Observation' && option.valueCodeableConcept) {
        const wrappedObservation = ObservationWrapper(option);
        baseLabel = option?.valueCodeableConcept
          ? wrappedObservation?.getDisplayValueFromValueCodeableConcept()
          : '';
        const interpretation = option?.interpretation
          ? wrappedObservation?.getInterpretationDisplay()
          : null;
        if (interpretation) {
          priorityInfo = ` (${interpretation})`;
        }
      } else if (option.resourceType === 'Condition') {
        const wrappedCondition = ConditionWrapper(option);
        baseLabel = option.code?.text ?? '';
        const severity = option?.severity ? wrappedCondition?.getSeverityDisplay() : null;
        if (severity) {
          priorityInfo = ` (${severity})`;
        }
      } else {
        baseLabel = option.code?.text ?? '';
      }
      return (baseLabel + priorityInfo).toLowerCase();
    };

    return addressesFiltered.sort((a, b) => {
      const labelA = getAddressLabel(a);
      const labelB = getAddressLabel(b);
      return labelA.localeCompare(labelB);
    });
  }, [conditions, observations, addressesFilter]);

  const [documentReferences, { create: createDocumentReference }] = useDocumentReferences({
    filter: {
      related: propReferral?.fhirId,
      autofetch: propReferral?.fhirId ? true : false,
    },
    pagination: {
      pageSize: 10,
    },
  });

  const handleClose = () => {
    onCancel();
  };

  const getHealthCareServices = useCallback(
    async (searchParams: any = {}) => {
      try {
        if (planDefinitionInternal && performerCategory?.value === 'practitioner') {
          let organizationIds: string[] = [];

          if (showServicesCheckbox) {
            const patientManagingOrgId = getReferenceId(patient?.managingOrganization?.reference);

            if (internalExternal.includes('internal') && !internalExternal.includes('external')) {
              if (patientManagingOrgId) {
                organizationIds = [patientManagingOrgId];
              }
            } else if (
              internalExternal.includes('external') &&
              !internalExternal.includes('internal')
            ) {
              if (patientManagingOrgId) {
                try {
                  const affiliatedOrganizations = await fetchOrganizationAffiliation([
                    patientManagingOrgId as string,
                  ]);
                  organizationIds = affiliatedOrganizations;
                } catch (error) {
                  return [];
                }
              }
            } else {
              if (patientManagingOrgId) {
                try {
                  const affiliatedOrganizations = await fetchOrganizationAffiliation([
                    patientManagingOrgId as string,
                  ]);
                  organizationIds = [patientManagingOrgId, ...affiliatedOrganizations];
                } catch (error) {
                  organizationIds = [patientManagingOrgId];
                }
              }
            }
          } else {
            const userOrganizations = authUser?.organizations ?? [];
            const affiliations = await fetchOrganizationAffiliation(userOrganizations);
            organizationIds = [...userOrganizations, ...affiliations];
          }

          const internalSearchParams = {
            ...searchParams,
            organizationId: organizationIds.length > 0 ? organizationIds : null,
          };

          const services = await ReferralService.getServicesByFilters(internalSearchParams);

          if (!services?.length) return [];

          const distinctServices = services.reduce<ServiceByFiltersResponse[]>(
            (accumServices, service) => {
              if (
                !!accumServices.find(
                  (serv) => serv?.healthcareService?.id === service?.healthcareService?.id
                )
              )
                return accumServices;
              return [...accumServices, service];
            },
            []
          );
          return distinctServices.map((service) => ({
            label: `${service?.healthcareService?.name}${
              service?.organization?.name ? ` | ${service?.organization?.name}` : ''
            } `,
            value: {
              healthcareService: service?.healthcareService
                ? HealthcareServiceWrapper(service?.healthcareService)
                : undefined,
              organization: service?.organization
                ? OrganizationWrapper(service?.organization)
                : undefined,
            },
          }));
        } else {
          const services = await ReferralService.getServicesByFilters(searchParams);
          if (!services?.length) return [];
          const distinctServices = services.reduce<ServiceByFiltersResponse[]>(
            (accumServices, service) => {
              if (
                !!accumServices.find(
                  (serv) => serv?.healthcareService?.id === service?.healthcareService?.id
                )
              )
                return accumServices;
              return [...accumServices, service];
            },
            []
          );
          return distinctServices.map((service) => ({
            label: `${service?.healthcareService?.name}${
              service?.organization?.name ? ` | ${service?.organization?.name}` : ''
            } `,
            value: {
              healthcareService: service?.healthcareService
                ? HealthcareServiceWrapper(service?.healthcareService)
                : undefined,
              organization: service?.organization
                ? OrganizationWrapper(service?.organization)
                : undefined,
            },
          }));
        }
      } catch (error) {
        return [];
      }
    },
    [
      planDefinitionInternal,
      performerCategory?.value,
      authUser?.organizations,
      showServicesCheckbox,
      internalExternal,
      patient?.managingOrganization,
    ]
  );

  const addressesSuggestionsFilter = useMemo(() => {
    const addressesCondition = filterResources(conditionSuggestions, {
      Condition: {
        values: addressesFilter?.conditionFilter ?? [],
        extractor: (item) => getIdFromURI(item.code?.coding?.[0]?.system ?? '') ?? undefined,
      },
    });
    const addressesObs = filterResources(observationSuggestions, {
      Observation: {
        values: addressesFilter?.observationFilter ?? [],
        extractor: (item) => item.category?.[0]?.coding?.[0]?.code,
      },
    });

    return { addressesCondition, addressesObs };
  }, [conditionSuggestions, observationSuggestions, addressesFilter]);

  const closeSelectors = useCallback(() => {
    updateState({ openSearch: false });
  }, [updateState]);

  useEffect(() => {
    if (!openAdd) return;

    updateState(
      defaultStateFieldsValues(authUser.user_fhir_uri, propReferral, {
        intentValueSet: requestIntentVS?.asList?.() ?? [],
        categoryValueSet: requestCategoryVS?.asListAll?.() ?? [],
        statusValueSet: referralStatus?.asListAll?.() ?? [],
        performerCategoryValues: performerCategoryValues,
        observations,
        conditions,
        observationSuggestions: addressesSuggestionsFilter?.addressesObs ?? [],
        conditionSuggestions: addressesSuggestionsFilter?.addressesCondition ?? [],
      })
    );

    closeSelectors();

    const getAsyncStateValues = async () => {
      if (!propReferral) return;
      setIsHealthcareLoading(true);
      const serviceRequest = propReferral as ServiceRequest;
      const getPerformerCategory = performerCategoryFromMetaTag(
        serviceRequest,
        performerCategoryValues
      );
      const healthCareServiceId = serviceRequest?.performer
        ?.find?.((performer) => performer?.reference?.split?.('/')?.[0] === 'HealthcareService')
        ?.reference?.split?.('/')?.[1];
      const performerRef = serviceRequest?.performer?.find?.(
        (performer) => performer?.reference?.split?.('/')?.[0] !== 'HealthcareService'
      );

      if (performerRef?.reference && !performerRef?.display) {
        try {
          const performerResource: any = await fhirClient.getResource(performerRef.reference);
          if (performerResource) {
            let displayName = '';
            const resourceType = performerRef.reference.split('/')[0];

            if (resourceType === 'PractitionerRole') {
              const wrapped = PractitionerRoleWrapper(performerResource);
              displayName =
                wrapped.getPractitionerName?.() || wrapped.getOrganizationName?.() || '';
            } else if (resourceType === 'Practitioner') {
              const wrapped = PractitionerWrapper(performerResource);
              displayName = wrapped.getFullName?.() || '';
            } else if (resourceType === 'Organization') {
              const wrapped = OrganizationWrapper(performerResource);
              displayName = wrapped.name || '';
            }

            if (displayName) {
              const referredToItem = getTaskOwnerByReference({
                reference: performerRef.reference,
                display: displayName,
              });
              updateState({ referredToItem });
            }
          }
        } catch (error) {
          // Error handled
        }
      }

      const planDefinitionId = serviceRequest?.instantiatesCanonical?.find?.(
        (instantiatesCanonical) => instantiatesCanonical?.split?.('/')?.[0] === 'PlanDefinition'
      );

      const getService = planDefinition?.find((e) => e?.reference === planDefinitionId);

      if (healthCareServiceId) {
        const isInternalPractitioner =
          getService?.planDefinitionType === 'internal' &&
          getPerformerCategory?.value === 'practitioner';

        let healthCareService;
        if (isInternalPractitioner) {
          const patientManagingOrgId = getReferenceId(patient?.managingOrganization?.reference);
          let organizationIds: string[] = [];

          if (patientManagingOrgId) {
            try {
              const affiliatedOrganizations = await fetchOrganizationAffiliation([
                patientManagingOrgId as string,
              ]);
              organizationIds = [patientManagingOrgId, ...affiliatedOrganizations];
            } catch (error) {
              organizationIds = [patientManagingOrgId];
            }
          }

          const searchParams = {
            serviceId: healthCareServiceId,
            organizationId: organizationIds.length > 0 ? organizationIds : null,
          };
          const rawServices = await ReferralService.getServicesByFilters(searchParams);

          // Transform the response to match the expected format
          if (rawServices && rawServices.length > 0) {
            healthCareService = rawServices.map((service) => {
              const serviceName =
                service?.healthcareService?.name || service?.healthcareService?.text || '';
              const orgName = service?.organization?.name || '';

              return {
                label: orgName ? `${serviceName} | ${orgName}` : serviceName,
                value: {
                  healthcareService: service?.healthcareService
                    ? HealthcareServiceWrapper(service?.healthcareService)
                    : undefined,
                  organization: service?.organization
                    ? OrganizationWrapper(service?.organization)
                    : undefined,
                },
              };
            });
          } else {
            // If organization filtering excludes the service, try to load it without filtering
            const fallbackServices = await getHealthCareServices({
              serviceId: healthCareServiceId,
            });
            healthCareService = fallbackServices.length > 0 ? fallbackServices : [];
          }
        } else {
          healthCareService = await getHealthCareServices({ serviceId: healthCareServiceId });
        }

        const serviceTypeItem = healthCareService?.[0] ?? null;

        // For internal practitioner services, always try to load from healthcare services first
        if (!serviceTypeItem && isInternalPractitioner) {
          // Try to load all healthcare services to find this one
          const allServices = await getHealthCareServices();
          const matchingService = allServices.find(
            (service) => service.value?.healthcareService?.id === healthCareServiceId
          );

          const finalServiceItem = matchingService || {
            label: serviceRequest?.code?.text || serviceRequest?.code?.coding?.[0]?.display || '',
            value: {
              healthcareService: serviceRequest?.code as CodeableConcept,
              organization: undefined,
            },
          };

          updateState({
            instantiatesCanonical: getService,
            serviceTypeItem: finalServiceItem,
            ...(!getPerformerCategory?.value &&
              getService?.planDefinitionPerformerCategory && {
                performerCategory: getService?.planDefinitionPerformerCategory,
              }),
          });
        } else if (!serviceTypeItem && serviceRequest?.code) {
          const fallbackServiceItem = {
            label: serviceRequest?.code?.text || serviceRequest?.code?.coding?.[0]?.display || '',
            value: {
              healthcareService: serviceRequest?.code as CodeableConcept,
              organization: undefined,
            },
          };

          updateState({
            instantiatesCanonical: getService,
            serviceTypeItem: fallbackServiceItem,
            ...(!getPerformerCategory?.value &&
              getService?.planDefinitionPerformerCategory && {
                performerCategory: getService?.planDefinitionPerformerCategory,
              }),
          });
        } else {
          updateState({
            instantiatesCanonical: getService,
            serviceTypeItem: serviceTypeItem,
            ...(!getPerformerCategory?.value &&
              getService?.planDefinitionPerformerCategory && {
                performerCategory: getService?.planDefinitionPerformerCategory,
              }),
          });
        }
      } else if (!healthCareServiceId) {
        const setServiceTypeItem = {
          label: serviceRequest?.code?.text || serviceRequest?.code?.coding?.[0]?.display || '',
          value: {
            healthcareService: serviceRequest?.code as CodeableConcept,
            organization: undefined,
          },
        };
        updateState({
          instantiatesCanonical: getService,
          serviceTypeItem: setServiceTypeItem,
          ...(!getPerformerCategory?.value &&
            getService?.planDefinitionPerformerCategory && {
              performerCategory: getService?.planDefinitionPerformerCategory,
            }),
        });
      }
      setIsHealthcareLoading(false);
    };
    getAsyncStateValues();
  }, [
    openAdd,
    propReferral,
    requestCategoryVS,
    requestIntentVS,
    referralStatus,
    performerCategoryValues,
    addressesAll,
    addressesSuggestionsFilter,
    planDefinition,
    authUser?.user_fhir_uri,
    conditions,
    observations,
    patient?.managingOrganization?.reference,
  ]);

  useEffect(() => {
    if (!openAdd || isLoadingVS) return;
    const getPlanDefinitionActivityDefinitionAsync = async () => {
      const mapPlanActivityDefinition = await ReferralService.getPlanActivityDefinition(
        requestIntentVS,
        referralServicesInternal,
        referralStatus
      );

      const sortedPlanDefinitions = mapPlanActivityDefinition.sort((a, b) => {
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        return titleA.localeCompare(titleB);
      });

      setPlanDefinition(sortedPlanDefinitions);
    };
    getPlanDefinitionActivityDefinitionAsync();
  }, [openAdd, requestIntentVS, referralServicesInternal, referralStatus, isLoadingVS]);

  const [, { find: findGoals }] = useGoals({
    map: GoalWrapper,
    autofetch: false,
  });
  const [, { find: findConditions }] = useConditions({
    map: ConditionWrapper,
    autofetch: false,
  });
  const [, { find: findObservations }] = useObservations({
    map: ObservationWrapper,
    autofetch: false,
  });

  const [taskGoalsAddresssess, setTaskGoalsAddresssess] = useState<
    (WrappedObservation | WrappedCondition)[]
  >([]);

  const getGoalsByPathWay = useCallback(
    (goalIds: string[]) => {
      const fetchData = async () => {
        const goals = await findGoals({
          _id: goalIds.join(','),
        });
        if (goals) {
          const addressess = removeNullAndUndefinedStrict(
            goals.flatMap((goal) => {
              // Check if goal.addresses exists and is an array before mapping
              if (goal.addresses) {
                return goal.addresses.map((ref) => ref.reference);
              }
              return [];
            })
          );
          const observationUuids: string[] = [];
          const conditionUuids: string[] = [];

          for (const item of addressess) {
            if (item.startsWith('Observation/')) {
              const uuid = item.substring('Observation/'.length);
              observationUuids.push(uuid);
            } else if (item.startsWith('Condition/')) {
              const uuid = item.substring('Condition/'.length);
              conditionUuids.push(uuid);
            }
          }

          const obs =
            observationUuids.length > 0
              ? await findObservations({
                  _id: observationUuids.join(','),
                })
              : [];
          const conds =
            conditionUuids.length > 0
              ? await findConditions({
                  _id: conditionUuids.join(','),
                })
              : [];
          const addressessArray = [...(conds || []), ...(obs || [])];
          setTaskGoalsAddresssess(addressessArray);
        }
      };

      fetchData();
    },
    [findGoals, findObservations, findConditions, setTaskGoalsAddresssess]
  );

  function removeNullAndUndefinedStrict<T>(arr: (T | null | undefined)[]): T[] {
    // item != null checks for both null and undefined
    return arr.filter((item): item is T => item != null);
  }

  useEffect(() => {
    const addressesFiltered = filterResources(taskGoalsAddresssess, {
      Condition: {
        values: addressesFilter?.conditionFilter ?? [],
        extractor: (item) => getIdFromURI(item.code?.coding?.[0]?.system ?? '') ?? '',
      },
      Observation: {
        values: addressesFilter?.observationFilter ?? [],
        extractor: (item) => item.category?.[0]?.coding?.[0]?.code,
      },
    });
    updateState({
      addresses: [...(addressesFiltered ?? [])],
    });
  }, [taskGoalsAddresssess, addressesFilter, updateState]);

  useEffect(() => {
    if (!externalValues?.planDefinition || propReferral) return;

    const getPlanDefinitionFromExternal = planDefinition?.find(
      (p) => getReferenceResourceId(p?.reference) === externalValues?.planDefinition
    );

    if (getPlanDefinitionFromExternal) {
      updateState({
        instantiatesCanonical: getPlanDefinitionFromExternal,
        intent: getPlanDefinitionFromExternal?.serviceRequestIntent,
        statusItem: getPlanDefinitionFromExternal?.serviceRequestStatus,
        status: getPlanDefinitionFromExternal?.serviceRequestStatus?.code,
        ...(getPlanDefinitionFromExternal?.serviceRequestCode?.value
          ? { serviceTypeItem: getPlanDefinitionFromExternal?.serviceRequestCode }
          : { serviceTypeItem: null }),
        ...(getPlanDefinitionFromExternal?.planDefinitionPerformerCategory
          ? { performerCategory: getPlanDefinitionFromExternal?.planDefinitionPerformerCategory }
          : { performerCategory: null }),
      });
    }
  }, [planDefinition, externalValues, propReferral, updateState]);

  const userOrganizations = useMemo(() => authUser?.organizations ?? [], [authUser?.organizations]);

  const orgsKey = useMemo(() => JSON.stringify(userOrganizations), [userOrganizations]);

  useEffect(() => {
    if (!openAdd) return;

    const getHealthCareServicesAsync = async () => {
      setIsHealthcareLoading(true);

      const services = await getHealthCareServices();

      setHealthCareServices(services);

      setIsHealthcareLoading(false);
    };
    getHealthCareServicesAsync();
  }, [
    openAdd,
    planDefinitionInternal,
    performerCategory?.value,
    showServicesCheckbox,
    internalExternal,
    patient?.managingOrganization,
  ]);

  useEffect(() => {
    const healthcareService = serviceTypeItem?.value?.healthcareService as WrappedHealthcareService;
    const newLocationIds = [
      ...(healthcareService?.location?.reduce?.<Set<string>>((locationIds, location) => {
        const locationId = location?.reference?.split?.('/')?.[1];
        if (locationId) locationIds.add(locationId);
        return locationIds;
      }, new Set()) ?? []),
    ];
    setLocationIds(newLocationIds);
  }, [serviceTypeItem]);

  const [locationRecords, { isFetching: isLocationsLoading }] = useLocations({
    filter: {
      ...(locationIds.length ? { _id: locationIds.join() } : {}),
    },
    autofetch: !!locationIds.length,
    pagination: {
      pageSize: 1000,
    },
  });

  const locations = useMemo(() => {
    if (!locationRecords?.length) return [];
    return locationRecords.map((location) => ({
      label: location?.name,
      value: {
        location,
      },
    }));
  }, [locationRecords]);

  const lastAttachment = useMemo(() => {
    if (!propReferral) return;
    const serviceRequest = propReferral as ServiceRequest;
    const lastDocumentReference = documentReferences?.find((document) => {
      const relatedItems = document?.context?.related;
      return relatedItems?.some(
        (relatedItem) =>
          relatedItem?.reference === `${serviceRequest?.resourceType}/${serviceRequest?.id}`
      );
    });
    if (lastDocumentReference?.content?.[0]?.attachment) {
      return {
        ...lastDocumentReference?.content?.[0]?.attachment,
        title: lastDocumentReference?.content?.[0]?.attachment?.title
          ? lastDocumentReference?.content?.[0]?.attachment?.title
          : `file_${lastDocumentReference?.id}`,
      };
    }
    return null;
  }, [documentReferences, propReferral]);

  const userListWithNames = useMemo(
    () =>
      users
        ?.filter((f: any) => !isEmpty(f.fhirUri.trim()))
        ?.map((user: any) => ({
          label: `${user.firstName} ${user.lastName}`,
          value: user.fhirUri,
        }))
        .sort((x1, x2) => (x1.label > x2.label ? 1 : x1.label < x2.label ? -1 : 0)) ?? [],
    [users]
  );

  const getAllUsers = useCallback(async () => {
    const result = await UserService.getUsersByOrgAndRole(userOrganizations, []);
    updateState({
      users: isArray(result) ? [...result] : [],
    });
  }, [orgsKey, updateState]);

  useEffect(() => {
    if (planDefinitionInternal) return;
    if (!orgsKey || orgsKey.length === 0) return;
    getAllUsers();
  }, [openAdd, instantiatesCanonical, orgsKey, getAllUsers, planDefinitionInternal]);

  useEffect(() => {
    if (propReferral?.note && isArray(propReferral.note)) {
      setAnnotations([...propReferral.note]);
    } else {
      setAnnotations([]);
    }
  }, [propReferral]);

  useEffect(() => {
    if (!showServicesCheckbox || !healthCareServices.length) {
      setFilteredHealthCareServices(healthCareServices);
      return;
    }

    if (internalExternal.includes('all')) {
      setFilteredHealthCareServices(healthCareServices);
      return;
    }

    const isInternal = internalExternal.includes('internal');
    const isExternal = internalExternal.includes('external');

    if (planDefinitionInternal && performerCategory?.value === 'practitioner') {
      setFilteredHealthCareServices(healthCareServices);
      return;
    }

    if (isInternal && !isExternal) {
      const patientManagingOrgId = getReferenceId(patient?.managingOrganization?.reference);

      const filtered = healthCareServices.filter((service) => {
        const serviceOrganization = service.value?.organization;

        if (!serviceOrganization) {
          return false; // Services without organization are not considered internal
        }

        return patientManagingOrgId === serviceOrganization.id;
      });

      setFilteredHealthCareServices(filtered);
    } else if (isExternal && !isInternal) {
      const patientManagingOrgId = getReferenceId(patient?.managingOrganization?.reference);

      const fetchExternalServices = async () => {
        let affiliatedOrganizations: string[] = [];

        if (patientManagingOrgId) {
          try {
            affiliatedOrganizations = await fetchOrganizationAffiliation([
              patientManagingOrgId as string,
            ]);
          } catch (error) {
            setFilteredHealthCareServices([]);
            return;
          }
        }

        const filtered = healthCareServices.filter((service) => {
          const serviceOrganization = service.value?.organization;

          if (!serviceOrganization) {
            return false; // Services without organization are not external
          }

          const isExternal =
            patientManagingOrgId !== serviceOrganization.id &&
            (serviceOrganization.id
              ? affiliatedOrganizations.includes(serviceOrganization.id)
              : false);

          return isExternal;
        });

        setFilteredHealthCareServices(filtered);
      };

      fetchExternalServices();
    } else {
      setFilteredHealthCareServices(healthCareServices);
    }
  }, [
    healthCareServices,
    internalExternal,
    showServicesCheckbox,
    patient?.managingOrganization,
    performerCategory?.value,
    planDefinitionInternal,
  ]);

  const validateRequiredFields = () => {
    const errors: {
      status?: boolean;
      intent?: boolean;
      service?: boolean;
      referredFromItem?: boolean;
      referredToItem?: boolean;
      performerCategory?: boolean;
      workflowScope?: boolean;
    } = {};
    if (!status) errors.status = true;
    if (!intent) errors.intent = true;
    if (!serviceTypeItem && serviceRequired) errors.service = true;
    if (!referredFromItem) {
      errors.referredFromItem = true;
    }
    if (
      !referredToItem &&
      (planDefinitionInternal || performerCategory?.value === 'practitioner') &&
      performerRequired
    ) {
      errors.referredToItem = true;
    }
    if (!performerCategory || !performerCategory?.value) errors.performerCategory = true;
    if (showWorkflowScope && !workflowScope) errors.workflowScope = true;

    if (Object.keys(errors).length) {
      updateState({ error: errors });
      return false;
    }
    return true;
  };

  const onClearAllPress = () => {
    updateState(defaultStateFieldsValues(authUser.user_fhir_uri, null));
  };

  const onSavePress = async () => {
    updateState({
      isLoading: true,
    });

    if (!validateRequiredFields()) {
      updateState({
        isLoading: false,
        success: null,
      });
      enqueueSnackbar('Please make sure to  fill required fields.', { variant: 'error' });
      return;
    }

    const time = `${date} ${startHour}`;

    const momentDate = moment(time);

    const inboundCategory = (referralPathCategory?.asListAll?.() ?? []).find(
      ({ code }) => code === 'inbound-referral'
    );
    const hasIntakeReferral = category?.some((e) => e?.code === 'intake-referral');

    const categoryEntries = [
      ...(category ?? []).map((cat) => ({
        coding: [cat],
        text: cat.display,
      })),
      ...(instantiatesCanonical?.topic ?? []),
      ...(hasIntakeReferral && inboundCategory?.code
        ? [
            {
              coding: [inboundCategory],
              text: inboundCategory.display,
            },
          ]
        : []),
    ];

    const newCategoryArray = new Set();
    const uniqueCategory = categoryEntries.filter(({ coding }) => {
      const code = coding?.[0]?.code;
      if (!code || newCategoryArray.has(code)) return false;
      newCategoryArray.add(code);
      return true;
    });

    const healthcareService = serviceTypeItem?.value?.healthcareService;

    const internalOrPractitioner =
      planDefinitionInternal || performerCategory?.value === 'practitioner';

    const validReferredItem = referredToItem && !referredToItem?.isAddOption;

    const isAddOption = referredToItem?.isAddOption;

    const hasHealthcareService = serviceTypeItem?.value?.healthcareService;

    const referenceResource = referredToItem as ReferenceResources;

    let serviceId: any = null;
    let serviceName: any = null;

    // Priority 1: Healthcare service (for internal practitioner services)
    if (hasHealthcareService?.id) {
      serviceId = `HealthcareService/${hasHealthcareService.id}`;
      serviceName = hasHealthcareService.name ?? serviceTypeItem?.label?.split(' | ')[0] ?? null;
    }
    // Priority 2: Add option
    else if (isAddOption) {
      serviceId = null;
      serviceName = referredToItem?.value ?? null;
    }
    // Priority 3: Practitioner reference (for non-healthcare service cases)
    else if (internalOrPractitioner && validReferredItem) {
      serviceId =
        !referenceResource?.reference && referredToItem?.resourceType
          ? `${referredToItem.resourceType}/${referredToItem.id}`
          : referenceResource?.reference ?? null;

      serviceName =
        !referenceResource?.reference && referredToItem?.resourceType
          ? getOwnerDisplayLabel(referredToItem)
          : referenceResource?.display ?? null;
    }

    const addressesRef = addresses?.map((x) => ({ reference: x.resourceType + '/' + x?.id }));

    let referredTo: any = null;
    if (internalOrPractitioner && validReferredItem) {
      referredTo =
        !referenceResource?.reference && referredToItem?.resourceType
          ? {
              reference: `${referredToItem.resourceType}/${referredToItem.id}`,
              display: getOwnerDisplayLabel(referredToItem),
            }
          : referenceResource?.reference
          ? {
              reference: referenceResource.reference,
              display: referenceResource.display || getOwnerDisplayLabel(referredToItem),
            }
          : null;
    }

    const payload: ReferralPayload = {
      id: propReferral?.id ?? null,
      date: momentDate.isValid() ? momentDate.toISOString() : undefined,
      start: startHour,
      status: statusItem?.code,
      intent: intent.code,
      patientId: patient?.id ?? null,
      ...(!planDefinitionInternal && { assignedTo: assignedToItem?.value }),
      referredFrom: referredFromItem
        ? !(referredFromItem as ReferenceResources)?.reference && referredFromItem?.resourceType
          ? {
              reference: `${referredFromItem?.resourceType}/${referredFromItem?.id}`,
              display: getOwnerDisplayLabel(referredFromItem),
            }
          : referredFromItem
        : null,
      referredTo, // Add the practitioner performer reference
      category: uniqueCategory?.length > 0 ? uniqueCategory : null,
      locationOrganization: null,
      locationId: locationOrganization?.value?.location?.id,
      locationName: locationOrganization?.value?.location?.name,
      services: null,
      service: null,
      serviceId,
      serviceType:
        healthcareService?.type?.[0] ??
        (healthcareService?.coding?.[0] || healthcareService?.text ? healthcareService : null),
      serviceName,
      instantiatesCanonical: instantiatesCanonical?.reference
        ? [instantiatesCanonical?.reference]
        : null,
      parentServiceRequest: serviceRequest,
      ...(carePlan?.id && { carePlanId: carePlan?.id }),
      performerCategory: performerCategory,
      reasonReference: addressesRef,
      reasonCode: propReferral?.reasonCode ?? null,
    };

    if (annotations && annotations.length > 0) {
      const note = annotations.map((x) => ({
        time: x?.time,
        text: x?.text,
        authorString: x?.authorString,
      }));
      payload.notes = note;
    }

    const result = await handleSaveReferral(payload);
    await onReferralSuccess?.({
      ...result,
      scope: workflowScope?.code,
    });
    handleClose();
  };

  const handleSaveReferral = async (payload: ReferralPayload) => {
    if (propReferral?.id) {
      const updatedReferral = await createReferral(payload);

      if (!isEmpty(updatedReferral) && selectedFile) {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = reader.result?.toString()?.replace(/^data:(.*,)?/, '');
          const newAttachment = await createAttachment(selectedFile, base64, updatedReferral);
          if (!newAttachment) {
            errorReferralCreation();
            enqueueSnackbar('Error uploading file.', { variant: 'error' });
          } else {
            completeReferralCreation();
            enqueueSnackbar('Referral saved successfully');
          }
        };
        reader.onerror = () => {
          errorReferralCreation();
          enqueueSnackbar('Error reading the selected file', { variant: 'error' });
        };
        reader.readAsDataURL(selectedFile);
      } else if (!isEmpty(updatedReferral)) {
        completeReferralCreation();
        enqueueSnackbar('Referral saved successfully');
        return updatedReferral;
      } else {
        errorReferralCreation();
        enqueueSnackbar(`${REFERRAL_CREATE_ERROR_MESSAGE}`, { variant: 'error' });
      }
    } else {
      try {
        let newReferrals = await createReferral(payload);

        if (!isEmpty(newReferrals) && selectedFile) {
          const reader = new FileReader();
          reader.onloadend = async () => {
            const base64 = reader.result?.toString()?.replace(/^data:(.*,)?/, '');
            const newAttachment = await createAttachment(selectedFile, base64, newReferrals);
            if (!newAttachment) {
              errorReferralCreation();
              enqueueSnackbar('Error uploading file.', { variant: 'error' });
            } else {
              completeReferralCreation();
              if (!showWorkflowScope) enqueueSnackbar('Referral saved successfully');
            }
          };
          reader.onerror = () => {
            errorReferralCreation();
            enqueueSnackbar('Error reading the selected file', { variant: 'error' });
          };
          reader.readAsDataURL(selectedFile);
        } else if (!isEmpty(newReferrals)) {
          completeReferralCreation();
          if (!showWorkflowScope) enqueueSnackbar('Referral saved successfully');
          return newReferrals;
        } else {
          errorReferralCreation();
          enqueueSnackbar(`${REFERRAL_CREATE_ERROR_MESSAGE}`, { variant: 'error' });
        }
      } catch (e) {
        errorReferralCreation();
        enqueueSnackbar(`${REFERRAL_CREATE_ERROR_MESSAGE}`, { variant: 'error' });
      }
    }
  };

  const createReferral = async (payload: any) => {
    const response = await ReferralService.createChildReferral(payload);
    return response;
  };

  const createAttachment = async (selectedFile: any, base64: any, relatedReferrals: any) => {
    const referralReferences = {
      reference: `ServiceRequest/${relatedReferrals?.id}`,
      type: 'ServiceRequest',
    };

    const attachmentPayload: any = {
      resourceType: 'DocumentReference',
      status: 'current',
      context: {
        related: [referralReferences],
      },
      subject: {
        reference: `Patient/${patient?.id}`,
        display: patient?.getFullName(),
        type: 'Patient',
      },
      author: [
        {
          reference: authUser.user_fhir_uri,
          display: authUser.name,
          type: 'Practitioner',
        },
      ],
      date: moment().format('YYYY-MM-DDTHH:MM:ssZ'),
      content: [
        {
          attachment: {
            contentType: selectedFile?.type,
            size: selectedFile?.size,
            data: base64,
            title: selectedFile?.name,
          },
        },
      ],
    };

    return createDocumentReference(attachmentPayload);
  };

  const completeReferralCreation = () => {
    updateState({
      success: 'Referral saved successfully' as any,
      isLoading: false,
    });
  };

  const errorReferralCreation = () => {
    updateState({
      isLoading: false,
      success: null,
    });
  };

  const handleDownloadAttachment = async () => {
    if (lastAttachment) {
      const element = document.createElement('a');
      const title = lastAttachment?.title ? lastAttachment?.title : `file_${serviceRequest?.id}`;
      element.setAttribute(
        'href',
        `data:${lastAttachment?.contentType};base64,${lastAttachment?.data}`
      );
      element.setAttribute('download', `${title}.${lastAttachment.contentType?.split('/')[1]}`);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }
  };

  const isTwoColumnLayout = openSearch || isSearchMemberOpen || isNewServiceModalOpen;

  const onSaveAnnotation = async (data: any) => {
    setAnnotations((originalAnnotations) => [...originalAnnotations, data]);
  };

  const handleViewAllAnnotation = () => {
    setAnnotationsOpen(true);
  };

  const handleHideAllAnnotation = () => {
    setAnnotationsOpen(false);
  };

  const annotationModalBreadcrumbs = [
    `${i18n('patients.details.tabs.referal', 'crs')}`,
    `${i18n('patients.details.serviceRequests.title', 'crs')}`,
    `${i18n('annotations.details.title', 'crs')}`,
  ];

  const AnnotationsModalHeader = () =>
    !!patient ? (
      <InfoRibbon containerSx={{ height: '60px', marginTop: 0, flexWrap: 'wrap' }} xs={12}>
        <Grid container item xs={5} pl={3}>
          <InfoRibbonPatient patient={patient} configurations={configurations?.mpi} />
        </Grid>
        <Divider orientation="vertical" flexItem sx={{ mr: '-1px', borderStyle: 'dashed' }} />
        <Grid container item xs={7} pl={3}>
          <InfoRibbonServiceRequest serviceRequest={propReferral} i18n={i18n} />
        </Grid>
      </InfoRibbon>
    ) : null;

  const handlerOpenPractitionerRoleModal = async (newPractitioner: any) => {
    if (newPractitioner) {
      const wrappedPractitioner = PractitionerWrapper(newPractitioner);
      setPractitioner(wrappedPractitioner);
      enqueueSnackbar('Practitioner created successfully', { variant: 'success' });
      setOpenPractitionerRolModal(true);
    }
  };

  const handlePractitionerRoleCreation = async (newPractitionerRole: any) => {
    try {
      if (!practitioner || !newPractitionerRole) {
        enqueueSnackbar('No practitioner available for role creation', { variant: 'error' });
        return;
      }

      const practitionerRoleWrapper = PractitionerRoleWrapper(newPractitionerRole);
      if (inputSelected) {
        updateState({
          [inputSelected]: practitionerRoleWrapper,
          isSearchMemberOpen: false,
        });
      }
      setOpenPractitionerRolModal(false);
      setIsNewPractitionerModalOpen(false);
      enqueueSnackbar('Practitioner role created successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error creating practitioner role', { variant: 'error' });
    }
  };

  return (
    <>
      {annotationsOpen ? (
        <AnnotationsModal
          open={annotationsOpen}
          isLoading={false}
          handlerSave={onSaveAnnotation}
          annotations={annotations}
          breadcrumbs={annotationModalBreadcrumbs}
          onClose={handleHideAllAnnotation}
          patient={patient}
          modalHeader={<AnnotationsModalHeader />}
        />
      ) : null}

      <CustomModal
        open={openAdd}
        onCancel={handleClose}
        onSave={onSavePress}
        className="vertical-overflow"
        breadcrumbs={[' ']}
        title={propReferral ? `Edit ${title}` : `New ${title}`}
        containerSx={[isTwoColumnLayout ? { width: '90vw' } : { width: '55vw' }]}
        isLoading={isLoading || isHealthcareLoading}
      >
        <Grid container spacing={1}>
          <Grid item xs={isTwoColumnLayout ? 5 : 12}>
            {showWorkflowScope && (
              <WorkflowForm
                show={showWorkflowScope}
                i18n={i18n}
                updateState={updateState}
                serviceRequest={propReferral ?? null}
                error={!!error?.workflowScope}
              />
            )}

            <GridSection>
              <GridItem>
                <TextField
                  fullWidth
                  value={date}
                  label={i18n('referral.details.ocurrenceDate', 'crs')}
                  defaultValue={date}
                  onChange={(e) => updateState({ date: e.target.value })}
                  type="date"
                  variant="outlined"
                />
              </GridItem>
              <GridItem>
                <TextField
                  fullWidth
                  label={i18n('referral.details.ocurrenceHour', 'crs')}
                  value={startHour}
                  defaultValue={startHour}
                  onChange={(e) => updateState({ startHour: e.target.value })}
                  type="time"
                  variant="outlined"
                />
              </GridItem>
            </GridSection>
            <GridSection>
              <GridItem>
                <Autocomplete
                  disabled={isLoading || (instantiatesCanonical && instantiatesCanonical?.isNAT)}
                  value={instantiatesCanonical}
                  fullWidth
                  onChange={(_: any, value: any | null) => {
                    updateState({
                      instantiatesCanonical: value,
                      ...(value?.serviceRequestIntent && { intent: value?.serviceRequestIntent }),
                      ...(value?.serviceRequestStatus && {
                        statusItem: value?.serviceRequestStatus,
                      }),
                      ...(value?.serviceRequestStatus?.code && {
                        status: value?.serviceRequestStatus?.code,
                      }),
                      ...(value?.serviceRequestCode?.value
                        ? { serviceTypeItem: value?.serviceRequestCode }
                        : { serviceTypeItem: null }),
                      ...(value?.planDefinitionPerformerCategory
                        ? { performerCategory: value?.planDefinitionPerformerCategory }
                        : { performerCategory: null }),
                    });
                  }}
                  options={
                    instantiatesCanonical && instantiatesCanonical?.isNAT
                      ? planDefinition
                      : planDefinition.filter((e) => !e?.isNAT)
                  }
                  getOptionLabel={({ title }) => title}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={i18n('referral.details.planDefinition', 'crs')}
                      variant="outlined"
                    />
                  )}
                />
              </GridItem>
              <GridItem>
                <Autocomplete
                  disabled={!!instantiatesCanonical?.planDefinitionPerformerCategory?.value}
                  fullWidth
                  disablePortal
                  options={performerCategoryValues ?? []}
                  value={performerCategory}
                  onChange={(_, value) => {
                    if (value)
                      updateState((state) => ({
                        error: { ...(state?.error ?? {}), performerCategory: null },
                      }));
                    if (
                      instantiatesCanonical !== null &&
                      instantiatesCanonical?.planDefinitionPerformerCategory?.value
                    ) {
                      updateState({
                        instantiatesCanonical: null,
                        performerCategory: value,
                      });
                    } else {
                      updateState({ performerCategory: value });
                    }
                    updateState({ isSearchMemberOpen: false, openSearch: false });
                  }}
                  getOptionLabel={(option) => option?.label ?? ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error?.performerCategory}
                      helperText={
                        !!error?.performerCategory && i18n('errorMessages.fieldIsRequired', 'crs')
                      }
                      label={`${i18n('referral.details.performerCategory', 'crs')}*`}
                      variant="outlined"
                    />
                  )}
                />
              </GridItem>
            </GridSection>
            {(addressesAll?.length > 0 || addresses?.length > 0) && (
              <GridSection>
                <GridItem xs={12}>
                  <Autocomplete
                    disabled={isLoading}
                    multiple
                    value={addresses}
                    fullWidth
                    onChange={(_: React.SyntheticEvent, newValue) => {
                      updateState({
                        addresses: newValue,
                      });
                    }}
                    options={addressesAll}
                    getOptionLabel={(option: WrappedObservation | WrappedCondition) => {
                      let baseLabel = '';
                      let priorityInfo = '';

                      if (option.resourceType === 'Observation' && option.valueCodeableConcept) {
                        const wrappedObservation = ObservationWrapper(option);
                        baseLabel = option?.valueCodeableConcept
                          ? wrappedObservation?.getDisplayValueFromValueCodeableConcept()
                          : '';
                        const interpretation = option?.interpretation
                          ? wrappedObservation?.getInterpretationDisplay()
                          : null;
                        if (interpretation) {
                          priorityInfo = ` (${interpretation})`;
                        }
                      } else if (option.resourceType === 'Condition') {
                        const wrappedCondition = ConditionWrapper(option);
                        baseLabel = option.code?.text ?? '';
                        const severity = option?.severity
                          ? wrappedCondition?.getSeverityDisplay()
                          : null;
                        if (severity) {
                          priorityInfo = ` (${severity})`;
                        }
                      } else {
                        baseLabel = option.code?.text ?? '';
                      }

                      return baseLabel + priorityInfo;
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={i18n('referral.details.addresses', 'crs')}
                        variant="outlined"
                      />
                    )}
                  />
                </GridItem>
              </GridSection>
            )}
            <GridSection>
              <GridItem>
                <Autocomplete
                  disablePortal
                  options={referralStatus?.asList() ?? []}
                  value={statusItem}
                  getOptionLabel={(item) => item?.display ?? ''}
                  onChange={(_: any, value: any | null) => {
                    if (value)
                      updateState((state) => ({
                        error: {
                          ...(state?.error ?? {}),
                          status: null,
                        },
                      }));
                    updateState({ statusItem: value, status: value.code });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error?.status}
                      helperText={!!error?.status && i18n('errorMessages.fieldIsRequired', 'crs')}
                      variant="outlined"
                      label={`${i18n('referral.details.status', 'crs')}*`}
                    />
                  )}
                />
              </GridItem>
              <GridItem>
                <Autocomplete
                  fullWidth
                  disabled={!requestIntentVS}
                  value={intent}
                  onChange={(_: React.SyntheticEvent, intent) => {
                    if (intent)
                      updateState((state) => ({
                        error: {
                          ...(state?.error ?? {}),
                          intent: null,
                        },
                      }));
                    updateState({ intent });
                  }}
                  options={requestIntentVS?.asList?.() ?? []}
                  getOptionLabel={({ display }: ValueSetComposeIncludeConcept) => display ?? ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error?.intent}
                      helperText={!!error?.intent && i18n('errorMessages.fieldIsRequired', 'crs')}
                      label={`${i18n('referral.details.intent', 'crs')}*`}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {!requestIntentVS?.asList?.()?.length ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </GridItem>
            </GridSection>

            <GridSection>
              <GridItem>
                <Autocomplete
                  disabled={!requestCategoryVS}
                  value={category}
                  multiple
                  freeSolo
                  onChange={(_: React.SyntheticEvent, newCategories) => {
                    const internalCategories =
                      category?.filter((cat) => isInternalCategory(cat)) || [];
                    const userCategories =
                      newCategories?.filter((cat) => !isInternalCategory(cat)) || [];
                    const combinedCategories = [...internalCategories, ...userCategories];
                    updateState({ category: combinedCategories });
                  }}
                  options={filterOutInternalCategories(
                    requestCategoryVS?.asListAll?.()?.map((e) => e) ?? []
                  )}
                  getOptionLabel={({ display }) => display ?? ''}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  filterOptions={(options, params) =>
                    options?.filter((item) =>
                      item?.display?.toLowerCase().includes(params?.inputValue?.toLowerCase())
                    )
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const isInternal = isInternalCategory(option);
                      const tagProps = getTagProps({ index });
                      return (
                        <Chip
                          {...tagProps}
                          key={index}
                          size="small"
                          label={option?.display}
                          sx={getCategoryChipStyle(option)}
                          onDelete={isInternal ? undefined : tagProps.onDelete}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={i18n('referral.details.category', 'crs')}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {!requestCategoryVS?.asListAll?.()?.length ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </GridItem>
              <GridItem>
                <SelectSearchMember
                  patient={patient ?? undefined}
                  isLoading={isLoading || !patient}
                  valueSelected={referredFromItem}
                  labelText={'referral.details.secondaryReferrals.referredFrom'}
                  isRequired={true}
                  error={error?.referredFromItem}
                  helperText={
                    error?.referredFromItem && i18n('errorMessages.fieldIsRequired', 'crs')
                  }
                  options={
                    planDefinitionInternal || performerCategory?.value === 'practitioner'
                      ? searchMemberOptions?.referralReferredFromPDInternal
                      : searchMemberOptions?.referralReferredFrom ?? []
                  }
                  selectedOption={'referredFromItem'}
                  stateKey={'referredFromItem'}
                  updateSelectedMember={(values) =>
                    updateState({
                      ...values,
                      openSearch: false,
                    })
                  }
                  updateState={updateState}
                />
              </GridItem>
            </GridSection>

            {performerCategory?.value && (
              <>
                {showServicesCheckbox && (
                  <GridSection>
                    <GridItem xs={12}>
                      <FormControl fullWidth>
                        <FormGroup row>
                          {internalExternalOrg.map(({ code, display }) => (
                            <FormControlLabel
                              key={code}
                              control={
                                <Checkbox
                                  checked={internalExternal?.includes(code || '')}
                                  onChange={(e) => {
                                    const { checked } = e.target;
                                    setInternalExternal((prev) => {
                                      const newCode = code || '';
                                      if (checked) {
                                        if (newCode === 'all') {
                                          return ['all'];
                                        } else {
                                          const filtered = prev.filter((c) => c !== 'all');
                                          return [...filtered, newCode];
                                        }
                                      } else {
                                        return prev.filter((c) => c !== newCode);
                                      }
                                    });
                                  }}
                                />
                              }
                              label={i18n(
                                `patients.details.serviceRequests.searchService.${code}`,
                                'crs'
                              )}
                              sx={{ py: 1, flexGrow: 1 }}
                            />
                          ))}
                        </FormGroup>
                      </FormControl>
                    </GridItem>
                  </GridSection>
                )}
                <GridSection>
                  <GridItem xs={12}>
                    <Autocomplete
                      fullWidth
                      disabled={isHealthcareLoading}
                      options={(() => {
                        let baseOptions: any[] = [];

                        if (planDefinitionInternal && performerCategory?.value === 'practitioner') {
                          baseOptions = showServicesCheckbox
                            ? filteredHealthCareServices
                            : healthCareServices;
                        } else if (
                          planDefinitionInternal ||
                          performerCategory?.value === 'practitioner'
                        ) {
                          baseOptions =
                            referralServicesInternal?.asListAll()?.map((e) => ({
                              label: e?.display || '',
                              value: {
                                healthcareService: {
                                  coding: [{ ...e }],
                                  text: e?.display,
                                } as CodeableConcept,
                                organization: undefined,
                              },
                            })) ?? [];
                        } else {
                          baseOptions = showServicesCheckbox
                            ? filteredHealthCareServices
                            : healthCareServices;
                        }

                        // Always include the current serviceTypeItem if it exists and is not already in options
                        if (
                          serviceTypeItem &&
                          !baseOptions.find(
                            (opt) =>
                              opt.value?.healthcareService?.id ===
                                serviceTypeItem.value?.healthcareService?.id ||
                              opt.label === serviceTypeItem.label
                          )
                        ) {
                          baseOptions = [serviceTypeItem, ...baseOptions];
                        }

                        return baseOptions;
                      })()}
                      value={serviceTypeItem}
                      getOptionLabel={(option: any) => {
                        const optionAny = option as any;
                        if (optionAny?.isSectionHeader) {
                          return '';
                        }
                        return option.label || '';
                      }}
                      isOptionEqualToValue={(option: any, value: any) => {
                        const optionAny = option as any;
                        const valueAny = value as any;
                        // Don't match section headers
                        if (optionAny?.isSectionHeader || valueAny?.isSectionHeader) {
                          return false;
                        }
                        return option.value === value.value;
                      }}
                      onChange={(_: any, value: any | null) => {
                        const optionAny = value as any;

                        // Prevent selection of section headers
                        if (optionAny?.isSectionHeader) {
                          return;
                        }

                        // Check if user selected "Add" option or "Add New Service" option
                        if (optionAny?.isAddOption) {
                          setPendingServiceName(optionAny.label);
                          setIsNewServiceModalOpen(true);
                          // Clear the service field to prevent displaying the option text
                          updateState({ serviceTypeItem: null, locationOrganization: null });
                          return;
                        }
                        if (optionAny?.isAddNewServiceOption) {
                          setPendingServiceName('');
                          setIsNewServiceModalOpen(true);
                          // Clear the service field to prevent displaying the option text
                          updateState({ serviceTypeItem: null, locationOrganization: null });
                          return;
                        }

                        if (value && value?.value) {
                          updateState((state) => ({
                            error: {
                              ...(state?.error ?? {}),
                              service: null,
                            },
                          }));
                        }
                        updateState({ serviceTypeItem: value, locationOrganization: null });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!error?.service}
                          helperText={
                            !!error?.service && i18n('errorMessages.fieldIsRequired', 'crs')
                          }
                          label={`
                            ${i18n('referral.details.secondaryReferrals.service', 'crs')} 
                            ${serviceRequired ? '*' : ''}
                          `}
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                {isHealthcareLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.startAdornment}
                              </>
                            ),
                            endAdornment: !planDefinitionInternal && (
                              <>
                                <IconButton
                                  onClick={() => {
                                    closeSelectors();

                                    updateState({
                                      openSearch: true,
                                      isSearchMemberOpen: false,
                                    });
                                  }}
                                >
                                  <Iconify icon="ic:baseline-search" />
                                </IconButton>
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      {...(addServiceManually
                        ? {
                            filterOptions: (options, state) => {
                              const searchValue = state.inputValue?.toLowerCase() || '';

                              // Separate services into regular and manually added
                              const regularServices: any[] = [];
                              const manuallyAddedServices: any[] = [];

                              options.forEach((option: any) => {
                                const healthcareService = option.value
                                  ?.healthcareService as WrappedHealthcareService;
                                // Check if service has ph-manually-added tag
                                const isManuallyAdded = healthcareService?.meta?.tag?.some?.(
                                  (tag: any) => tag?.code === 'ph-manually-added'
                                );

                                const matchesSearch = option.label
                                  ?.toLowerCase()
                                  .includes(searchValue);

                                if (matchesSearch) {
                                  if (isManuallyAdded) {
                                    manuallyAddedServices.push(option);
                                  } else {
                                    regularServices.push(option);
                                  }
                                }
                              });

                              const resultOptions: any[] = [];

                              // Add regular services section
                              if (regularServices.length > 0) {
                                resultOptions.push({
                                  label: 'Services',
                                  isSectionHeader: true,
                                  sectionType: 'regular',
                                });
                                resultOptions.push(...regularServices);
                              }

                              // Add manually entered services section
                              if (manuallyAddedServices.length > 0) {
                                resultOptions.push({
                                  label: 'Manually Entered',
                                  isSectionHeader: true,
                                  sectionType: 'manual',
                                });
                                resultOptions.push(...manuallyAddedServices);
                              }

                              // Add "Add New Service" option if user typed something that doesn't exist
                              const input = state.inputValue?.trim();
                              const alreadyExists = options.some(
                                (opt: any) => opt.label.toLowerCase() === input.toLowerCase()
                              );

                              if (input && !alreadyExists) {
                                resultOptions.push({
                                  label: input,
                                  value: {
                                    healthcareService: {
                                      text: input,
                                    } as CodeableConcept,
                                    organization: undefined,
                                  },
                                  isAddOption: true as any,
                                });
                              }

                              // Always add "(+) Add New Service" option at the end
                              resultOptions.push({
                                label: '(+) Add New Service',
                                value: {
                                  healthcareService: undefined,
                                  organization: undefined,
                                },
                                isAddNewServiceOption: true as any,
                              });

                              return resultOptions;
                            },
                            renderOption: (props: any, option: any) => {
                              const optionAny = option as any;

                              // Render section headers
                              if (optionAny?.isSectionHeader) {
                                return (
                                  <li
                                    {...props}
                                    style={{
                                      padding: '8px 16px',
                                      backgroundColor: '#f5f5f5',
                                      fontWeight: 600,
                                      fontSize: '0.875rem',
                                      cursor: 'default',
                                    }}
                                  >
                                    {optionAny.label}
                                  </li>
                                );
                              }

                              const handleOptionClick = (e: React.MouseEvent) => {
                                // For "Add" options, prevent default selection and trigger modal
                                if (optionAny?.isAddOption || optionAny?.isAddNewServiceOption) {
                                  e.preventDefault();
                                  e.stopPropagation();

                                  if (optionAny?.isAddOption) {
                                    setPendingServiceName(optionAny.label);
                                  } else {
                                    setPendingServiceName('');
                                  }
                                  setIsNewServiceModalOpen(true);
                                  // Clear the service field
                                  updateState({
                                    serviceTypeItem: null,
                                    locationOrganization: null,
                                  });

                                  // Close the dropdown
                                  setTimeout(() => {
                                    const input = document.activeElement as HTMLInputElement;
                                    if (input && input.tagName === 'INPUT') {
                                      input.blur();
                                    }
                                  }, 0);
                                  return;
                                }

                                // For normal options, use default behavior
                                if (props.onClick) {
                                  props.onClick(e);
                                }
                              };

                              return (
                                <li
                                  {...props}
                                  style={{
                                    paddingLeft: '32px',
                                    cursor:
                                      optionAny?.isAddOption || optionAny?.isAddNewServiceOption
                                        ? 'pointer'
                                        : 'default',
                                  }}
                                  onClick={handleOptionClick}
                                >
                                  {optionAny?.isAddOption ? (
                                    <>
                                      <Iconify icon="ic:baseline-add" style={{ marginRight: 8 }} />
                                      Add&nbsp;<strong>"{option.label}"</strong>
                                    </>
                                  ) : optionAny?.isAddNewServiceOption ? (
                                    <>
                                      <Iconify icon="ic:baseline-add" style={{ marginRight: 8 }} />
                                      Add New Service
                                    </>
                                  ) : (
                                    option.label
                                  )}
                                </li>
                              );
                            },
                          }
                        : {})}
                    />
                  </GridItem>
                </GridSection>

                <GridSection>
                  <GridItem>
                    {showComponent ? (
                      <Autocomplete
                        fullWidth
                        disabled={isLocationsLoading || locations.length < 1}
                        options={locations}
                        value={locationOrganization}
                        onChange={(_: any, value: any | null) => {
                          updateState({
                            locationOrganization: value,
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={i18n('referral.details.secondaryReferrals.location', 'crs')}
                            variant="outlined"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isLocationsLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    ) : (
                      <SelectSearchMember
                        patient={patient ?? undefined}
                        isLoading={isLoading || !patient}
                        valueSelected={referredToItem}
                        labelText={'referral.details.performer'}
                        isRequired={performerRequired}
                        error={error?.referredToItem}
                        helperText={
                          error?.referredToItem && i18n('errorMessages.fieldIsRequired', 'crs')
                        }
                        options={
                          planDefinitionInternal || performerCategory?.value === 'practitioner'
                            ? searchMemberOptions?.referralPerformerToItem
                            : []
                        }
                        selectedOption={'referredToItem'}
                        stateKey={'referredToItem'}
                        updateSelectedMember={(newState) => {
                          if (newState.referredToItem?.isAddOption) {
                            setIsNewPractitionerModalOpen(true);
                          } else {
                            updateState({
                              ...newState,
                              openSearch: false,
                            });
                          }
                        }}
                        updateState={updateState}
                        addManually={addPerformerManually}
                      />
                    )}
                  </GridItem>

                  {showComponent && (
                    <GridItem>
                      <Autocomplete
                        fullWidth
                        disablePortal
                        options={userListWithNames}
                        value={assignedToItem}
                        onChange={(_: any, value: any | null) =>
                          updateState({ assignedToItem: value })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={i18n('referral.details.assignedTo', 'crs')}
                            variant="outlined"
                          />
                        )}
                      />
                    </GridItem>
                  )}
                </GridSection>
              </>
            )}

            <GridSection sx={{ alignItems: 'center' }}>
              <GridItem xs={lastAttachment ? 5.9 : 12}>
                <InputLabel shrink>
                  {i18n('referral.details.secondaryReferrals.attachment', 'crs')}
                </InputLabel>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    value={file}
                    defaultValue={file}
                    onChange={(e) =>
                      updateState({
                        file: e.target.value as any,
                        selectedFile: (e.target as any)?.files[0],
                      })
                    }
                    variant="outlined"
                    type="file"
                  />
                </FormControl>
              </GridItem>
              {propReferral?.id && lastAttachment ? (
                <GridItem xs={5.9}>
                  <Stack direction="row" alignItems="center" mt={3}>
                    <Typography variant="caption">{lastAttachment?.title}</Typography>
                    <IconButton onClick={handleDownloadAttachment}>
                      <Iconify icon="bi:download" />
                    </IconButton>
                  </Stack>
                </GridItem>
              ) : null}
            </GridSection>

            <GridSection>
              <GridItem xs={12}>
                <AnnotationsList annotations={annotations} onViewAll={handleViewAllAnnotation} />
              </GridItem>
            </GridSection>
          </Grid>
          {openSearch && (
            <Grid item xs={6.9}>
              <SearchServices patient={patient} updateState={updateState} openSearch={openSearch} />
            </Grid>
          )}

          {isSearchMemberOpen && (
            <Grid item xs={6.9} sx={{ marginTop: 3 }}>
              <SearchMember
                patient={patient as WrappedPatient}
                onSelectResource={(resource) => {
                  updateState({
                    [inputSelected]: resource,
                    isSearchMemberOpen: false,
                  });
                }}
                externalResourceType={
                  inputSelected === 'referredFromItem'
                    ? getExternalResourceType(referredFromItem)
                    : getExternalResourceType(referredToItem) ?? undefined
                }
                onClear={() => updateState({ isSearchMemberOpen: false })}
                onAddNewPractitioner={() => setIsNewPractitionerModalOpen(true)}
              />
            </Grid>
          )}

          {addServiceManually && isNewServiceModalOpen && (
            <Grid item xs={6.9} sx={{ marginTop: 3 }}>
              <NewServiceModal
                open={isNewServiceModalOpen}
                onClose={() => {
                  setIsNewServiceModalOpen(false);
                  setPendingServiceName('');
                }}
                initialServiceName={pendingServiceName}
                onServiceCreated={(newService) => {
                  // Update state with the newly created service
                  updateState({
                    serviceTypeItem: newService,
                    locationOrganization: null,
                    error: {
                      ...(state?.error ?? {}),
                      service: null,
                    },
                  });
                  // Refresh healthcare services list
                  const getHealthCareServicesAsync = async () => {
                    setIsHealthcareLoading(true);
                    const services = await getHealthCareServices();
                    setHealthCareServices(services);
                    setIsHealthcareLoading(false);
                  };
                  getHealthCareServicesAsync();
                  setIsNewServiceModalOpen(false);
                  setPendingServiceName('');
                }}
              />
            </Grid>
          )}

          <Stack direction="row" justifyItems="center" spacing={2} sx={{ mt: 2 }}>
            <Button
              disabled={isLoading}
              onClick={onClearAllPress}
              variant="outlined"
              color="secondary"
              size="small"
            >
              {i18n('clear')}
            </Button>
          </Stack>
        </Grid>
      </CustomModal>

      {configurations && (
        <NewPractitionerModal
          open={isNewPractitionerModalOpen}
          onClose={() => {
            setIsNewPractitionerModalOpen(false);
          }}
          configurations={configurations}
          handlerActionCreate={handlerOpenPractitionerRoleModal}
        />
      )}

      {!!practitioner && (
        <CreatePractitionerRoleDialog
          onDialogClose={() => {
            setOpenPractitionerRolModal(false);
          }}
          isVisible={openPractitionerRolModal}
          selectedResource={practitioner}
          onPractitionerRoleCreation={handlePractitionerRoleCreation}
        />
      )}
    </>
  );
};

export default AddReferral;
